import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  ArrowsClockwise, Lightning, TrendUp, Funnel, MagnifyingGlass, Eye, Warning, Timer,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const SOURCE_COLORS = {
  meta_lead_form: "#1877F2",
  google_ads: "#EA4335",
  kraya_whatsapp: "#25D366",
  callyzer: "#FF9800",
  referrals: "#7C3AED",
  direct_walkin: "#0EA5E9",
  whatsapp_enquiry: "#22C55E",
  confirmed_appointment: "#A855F7",
  "338_number": "#F59E0B",
  "400_number": "#10B981",
  "2020_number": "#EF4444",
  ht_form: "#06B6D4",
  hair_treatment_form: "#0891B2",
  hyd_form: "#84CC16",
};

const RATE_BADGE = (val, good, bad) => {
  if (val == null) return "#52525B";
  if (val >= good) return "#008A00";
  if (val <= bad) return "#E00000";
  return "#FF9800";
};

const TOUCH_BADGE = (mins) => {
  if (mins == null) return "#52525B";
  if (mins <= 5) return "#008A00";        // <5 min — great
  if (mins <= 60) return "#FF9800";       // <1h — ok
  return "#E00000";                        // >1h — bleeding
};

export default function Sources() {
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ date_from: "", date_to: "", branch_id: "" });
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.date_from) params.date_from = filter.date_from;
      if (filter.date_to) params.date_to = filter.date_to;
      if (filter.branch_id) params.branch_id = filter.branch_id;
      const [f, b] = await Promise.all([
        api.get("/sources/funnel", { params }),
        branches.length ? Promise.resolve({ data: branches }) : api.get("/branches"),
      ]);
      setFunnel(f.data);
      setBranches(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const filteredSources = useMemo(() => {
    if (!funnel?.sources) return [];
    if (!search) return funnel.sources;
    const q = search.toLowerCase();
    return funnel.sources.filter((s) =>
      s.label.toLowerCase().includes(q) || s.source.toLowerCase().includes(q),
    );
  }, [funnel, search]);

  const maxLeads = useMemo(
    () => Math.max(1, ...filteredSources.map((s) => s.leads)),
    [filteredSources],
  );
  const maxP50 = useMemo(() => {
    const vals = filteredSources.map((s) => s.speed?.p50_min).filter((v) => v != null);
    return Math.max(1, ...vals);
  }, [filteredSources]);

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Source intelligence"
        title="Sources"
        description="See which channel is bringing leads — and where in the funnel they bleed."
        actions={
          <button onClick={load} data-testid="sources-refresh"
            className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2">
            <ArrowsClockwise size={16} /> Refresh
          </button>
        }
      />

      {/* Filters */}
      <div className="px-8 pt-6 flex flex-wrap gap-2 items-center" data-testid="sources-filters">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search source"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="sources-search"
            className="text-sm border border-border pl-9 pr-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-56"
          />
        </div>
        <select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}
          data-testid="sources-filter-branch"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white">
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
          data-testid="sources-filter-from"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white" />
        <span className="text-muted-foreground text-xs">→</span>
        <input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
          data-testid="sources-filter-to"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white" />
        {(filter.date_from || filter.date_to || filter.branch_id) && (
          <button
            onClick={() => setFilter({ date_from: "", date_to: "", branch_id: "" })}
            data-testid="sources-filter-clear"
            className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline"
          >Clear</button>
        )}
        {funnel && (
          <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid="sources-scope-note">
            Scope: <strong className="text-foreground">{funnel.scope}</strong>
          </span>
        )}
      </div>

      {/* Totals strip */}
      {funnel && (
        <div className="px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sources-totals">
          <Kpi label="Total leads" value={funnel.totals.leads} color="#002FA7" testid="kpi-total" icon={Funnel} />
          <Kpi label="Got touched" value={funnel.totals.connected}
            sub={`${funnel.totals.connect_rate}% connect rate`}
            color={RATE_BADGE(funnel.totals.connect_rate, 70, 40)} testid="kpi-connected" icon={Eye} />
          <Kpi label="Converted" value={funnel.totals.converted}
            sub={`${funnel.totals.conversion_rate}% conversion`}
            color={RATE_BADGE(funnel.totals.conversion_rate, 3, 1)} testid="kpi-converted" icon={TrendUp} />
          <Kpi label="Sources" value={funnel.sources.length}
            sub={`${LEAD_SOURCE_COUNT} configured`}
            color="#52525B" testid="kpi-sources" icon={Lightning} />
        </div>
      )}

      {/* Funnel table */}
      <div className="px-8 pt-6 pb-4">
        <h2 className="font-display text-base font-bold tracking-tight mb-3">
          Funnel by source
          {funnel && <span className="text-xs font-normal text-muted-foreground ml-2">
            Leads → Connected → Qualified → Appointment → Visited → Converted
          </span>}
        </h2>
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="sources-funnel-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold">Source</th>
                <th className="px-4 py-3 font-bold text-right">Leads</th>
                <th className="px-4 py-3 font-bold text-right">Connected</th>
                <th className="px-4 py-3 font-bold text-right">Qualified</th>
                <th className="px-4 py-3 font-bold text-right">Appt</th>
                <th className="px-4 py-3 font-bold text-right">Visited</th>
                <th className="px-4 py-3 font-bold text-right">Converted</th>
                <th className="px-4 py-3 font-bold text-right">Connect %</th>
                <th className="px-4 py-3 font-bold text-right">Conv %</th>
                <th className="px-4 py-3 font-bold text-right">Quality</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : filteredSources.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center text-sm text-muted-foreground">
                  No source data for this filter combination.
                </td></tr>
              ) : filteredSources.map((s) => (
                <tr key={s.source} className="border-b border-border last:border-0 hover:bg-secondary/40"
                  data-testid={`source-row-${s.source}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: SOURCE_COLORS[s.source] || "#52525B" }} />
                      <span className="font-medium">{s.label}</span>
                    </div>
                    {/* Lead-volume bar relative to max */}
                    <div className="mt-1.5 h-1 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full"
                        style={{
                          width: `${(s.leads / maxLeads) * 100}%`,
                          background: SOURCE_COLORS[s.source] || "#52525B",
                        }} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold">{s.leads.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.connected}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.qualified}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.appt_booked}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.visited}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold">{s.converted}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold"
                    style={{ color: RATE_BADGE(s.connect_rate, 70, 40) }}
                    data-testid={`source-${s.source}-connect`}>
                    {s.connect_rate}%
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold"
                    style={{ color: RATE_BADGE(s.conversion_rate, 3, 1) }}
                    data-testid={`source-${s.source}-conv`}>
                    {s.conversion_rate}%
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-block min-w-[3rem] px-2 py-1 text-xs font-bold tabular-nums"
                      style={{
                        color: RATE_BADGE(s.quality_score, 60, 30),
                        background: RATE_BADGE(s.quality_score, 60, 30) + "12",
                      }}
                      data-testid={`source-${s.source}-quality`}>
                      {s.quality_score}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Speed-to-lead histogram (A2) */}
      <div className="px-8 pt-4 pb-8" data-testid="speed-to-lead-section">
        <h2 className="font-display text-base font-bold tracking-tight mb-1 flex items-center gap-2">
          <Timer size={18} weight="bold" /> Speed to first touch
        </h2>
        <p className="text-xs text-muted-foreground mb-3 max-w-xl">
          Median minutes from lead arrival to first agent action, per source. Under
          5 minutes is healthy. Over an hour likely means you&apos;re losing them.
        </p>
        <div className="bg-white border border-border p-5 space-y-2.5" data-testid="speed-bars">
          {filteredSources.filter((s) => s.speed?.p50_min != null).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No touch-time data yet. Once agents start updating lead status, the histogram populates.
            </div>
          ) : (
            filteredSources
              .filter((s) => s.speed?.p50_min != null)
              .sort((a, b) => a.speed.p50_min - b.speed.p50_min)
              .map((s) => {
                const p50 = s.speed.p50_min;
                const p75 = s.speed.p75_min;
                const color = TOUCH_BADGE(p50);
                const widthP50 = `${Math.min(100, (p50 / maxP50) * 100)}%`;
                const widthP75 = `${Math.min(100, (p75 / maxP50) * 100)}%`;
                return (
                  <div key={s.source} className="flex items-center gap-3" data-testid={`speed-${s.source}`}>
                    <div className="w-44 text-xs font-medium truncate flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: SOURCE_COLORS[s.source] || "#52525B" }} />
                      {s.label}
                    </div>
                    <div className="flex-1 relative h-7 bg-secondary rounded-sm overflow-hidden">
                      {/* p75 ghost behind p50 */}
                      <div className="absolute inset-y-0 left-0 opacity-25"
                        style={{ width: widthP75, background: color }} />
                      <div className="absolute inset-y-0 left-0"
                        style={{ width: widthP50, background: color }} />
                      <div className="absolute inset-0 flex items-center px-2 text-xs font-bold tabular-nums text-white drop-shadow-sm">
                        {p50 < 1 ? `${Math.round(p50 * 60)}s`
                          : p50 < 60 ? `${p50.toFixed(1)}m`
                          : `${(p50 / 60).toFixed(1)}h`}
                        <span className="ml-2 opacity-75 font-normal">
                          (p75: {p75 < 1 ? `${Math.round(p75 * 60)}s`
                            : p75 < 60 ? `${p75.toFixed(1)}m`
                            : `${(p75 / 60).toFixed(1)}h`})
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums w-16 text-right">
                      {s.speed.samples} samples
                    </div>
                  </div>
                );
              })
          )}
          {/* Legend */}
          <div className="pt-3 mt-2 border-t border-border flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3" style={{ background: "#008A00" }} /> &lt; 5 min · healthy
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3" style={{ background: "#FF9800" }} /> 5 min – 1 hr · slow
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3" style={{ background: "#E00000" }} /> &gt; 1 hr · bleeding
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <Warning size={12} /> Solid bar = p50 (median) · Faded = p75
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hardcoded count of configured sources for the totals tile (kept in sync with
// LEAD_SOURCES in backend/server.py).
const LEAD_SOURCE_COUNT = 14;

function Kpi({ label, value, sub, color, icon: Icon, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
        {Icon && <Icon size={16} weight="bold" style={{ color }} />}
      </div>
      <div className="font-display text-3xl font-bold tabular-nums" style={{ color }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
